import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Package, Truck, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";
import { lookupOrder } from "@/lib/api";

const STATUS_BLOCKS = [
    { key: "ordered", label: "Order placed", icon: CheckCircle2 },
    { key: "paid", label: "Payment confirmed", icon: CheckCircle2 },
    { key: "packed", label: "Packed · discreet mailer", icon: Package },
    { key: "shipped", label: "In transit", icon: Truck },
    { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

function statusFor(order) {
    // Derive a 5-step state from the order shape
    const isPaid = order.payment_status === "paid";
    const tracked = !!order.tracking_number;
    // tracking_status comes from the Shippo webhook: PRE_TRANSIT / TRANSIT / DELIVERED / RETURNED / FAILURE / UNKNOWN
    const ts = (order.tracking_status || "").toUpperCase();
    const inTransit = tracked && (ts === "TRANSIT" || ts === "DELIVERED");
    const delivered = ts === "DELIVERED";
    return {
        ordered: true,
        paid: isPaid,
        packed: isPaid,
        shipped: tracked && (inTransit || ts === "PRE_TRANSIT" || ts === ""),
        // we light up "shipped" as soon as a label exists; "delivered" is its own milestone
        delivered,
    };
}

export default function TrackOrder() {
    const [orderId, setOrderId] = useState("");
    const [email, setEmail] = useState("");
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setOrder(null);
        if (!orderId || !email) {
            setError("Please enter both your order # and the email used at checkout.");
            return;
        }
        setBusy(true);
        try {
            const data = await lookupOrder({ order_id: orderId.trim(), email: email.trim() });
            setOrder(data);
        } catch (err) {
            setError(err?.response?.data?.detail || "Couldn't find that order. Double-check the order # and email.");
        }
        setBusy(false);
    };

    const flags = order ? statusFor(order) : null;

    return (
        <div className="bg-ink-base text-ink min-h-[80vh]" data-testid="track-order-page">
            <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
                <div className="flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-neon" />
                    <p className="overline text-neon">/ ORDER · LOOKUP</p>
                </div>
                <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95] tracking-tight">
                    Track <span className="text-neon">my order.</span>
                </h1>
                <p className="mt-6 text-ink-dim text-base max-w-xl leading-relaxed">
                    Enter your order number (from your confirmation email) and the email you checked out with. We'll show payment status, packing, and tracking — that's it. No account required.
                </p>

                <form onSubmit={submit} className="mt-10 border border-ink-strong bg-ink-surface p-6 sm:p-8 space-y-4" data-testid="track-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="overline text-[10px] text-ink-mute mb-2 block">ORDER #</span>
                            <input className="w-full border border-ink-strong bg-ink-base text-ink px-4 py-3 text-sm font-mono focus:outline-none focus:border-neon"
                                placeholder="e.g. a1b2c3d4 or full order id"
                                value={orderId} onChange={(e) => setOrderId(e.target.value)}
                                data-testid="track-order-id-input" />
                        </label>
                        <label className="block">
                            <span className="overline text-[10px] text-ink-mute mb-2 block">EMAIL</span>
                            <input type="email" className="w-full border border-ink-strong bg-ink-base text-ink px-4 py-3 text-sm focus:outline-none focus:border-neon"
                                placeholder="email@used-at-checkout.com"
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                data-testid="track-email-input" />
                        </label>
                    </div>
                    <button type="submit" disabled={busy}
                        className="w-full inline-flex items-center justify-center gap-2 bg-neon hover:bg-neon-glow text-black px-6 py-3.5 font-bold tracking-wide disabled:opacity-50"
                        data-testid="track-submit">
                        <Search className="w-4 h-4" /> {busy ? "LOOKING UP…" : "LOOK UP ORDER"}
                    </button>
                    {error && <p className="text-sm text-red-400 mt-2" data-testid="track-error">{error}</p>}
                </form>

                {order && flags && (
                    <div className="mt-10 border border-ink-strong bg-ink-surface" data-testid="track-result">
                        <div className="flex items-baseline justify-between p-6 border-b border-ink">
                            <div>
                                <p className="overline text-neon text-[10px]">ORDER #{order.short_id}</p>
                                <p className="font-mono text-xs text-ink-mute mt-1">{(order.created_at || "").slice(0, 16).replace("T", " ")}</p>
                            </div>
                            <div className="text-right">
                                <div className="font-display text-3xl font-bold text-neon">{order.currency} ${(order.amount || 0).toFixed(2)}</div>
                                <div className="overline text-[10px] text-ink-mute mt-1">{(order.payment_method || "card").toUpperCase()} · {order.payment_status}</div>
                            </div>
                        </div>

                        {/* status timeline */}
                        <div className="p-6 border-b border-ink">
                            <p className="overline text-gold mb-4">SHIPMENT STATUS</p>
                            <div className="grid grid-cols-5 gap-2">
                                {STATUS_BLOCKS.map((s) => {
                                    const active = flags[s.key];
                                    const Icon = active ? s.icon : Clock;
                                    return (
                                        <div key={s.key} className="text-center">
                                            <div className={`w-12 h-12 mx-auto border-2 flex items-center justify-center ${active ? "border-neon text-neon bg-neon/10" : "border-ink-strong text-ink-mute"}`}>
                                                <Icon className="w-5 h-5" strokeWidth={1.75} />
                                            </div>
                                            <p className={`mt-2 text-[10px] font-mono uppercase tracking-wider ${active ? "text-ink" : "text-ink-mute"}`}>{s.label}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            {order.tracking_status && (
                                <p className="mt-4 text-center text-xs text-ink-dim font-mono">
                                    LIVE STATUS · <span className="text-neon">{order.tracking_status.replace(/_/g, " ")}</span>
                                    {order.tracking_status_details ? <span className="text-ink-mute"> · {order.tracking_status_details}</span> : null}
                                </p>
                            )}
                        </div>

                        {/* tracking */}
                        <div className="p-6 border-b border-ink">
                            <p className="overline text-gold mb-3">TRACKING</p>
                            {order.tracking_number ? (
                                <div className="space-y-2">
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-ink-dim text-xs">Carrier</span>
                                        <span className="text-ink text-sm">{order.tracking_carrier || "Canada Post"}</span>
                                    </div>
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-ink-dim text-xs">Number</span>
                                        <span className="font-mono text-ink text-sm">{order.tracking_number}</span>
                                    </div>
                                    {order.tracking_url && (
                                        <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                                            className="mt-3 inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-black px-5 py-2.5 font-bold text-xs tracking-wide uppercase">
                                            Track with carrier <ArrowUpRight className="w-3.5 h-3.5" />
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-ink-dim">Not yet dispatched. We email a tracking link the moment your parcel is handed to the carrier (typically within 24–72h of payment confirmation).</p>
                            )}
                        </div>

                        {/* items */}
                        <div className="p-6">
                            <p className="overline text-gold mb-3">ITEMS · {order.items?.length || 0}</p>
                            <div className="divide-y divide-ink">
                                {(order.items || []).map((it, i) => (
                                    <div key={i} className="flex items-baseline justify-between py-2.5 text-sm">
                                        <div>
                                            {it.slug ? (
                                                <Link to={`/product/${it.slug}`} className="text-ink hover:text-neon transition-colors">{it.name}</Link>
                                            ) : (
                                                <span className="text-ink">{it.name}</span>
                                            )}
                                            <span className="text-ink-mute ml-2 text-xs">{it.variant_label}</span>
                                        </div>
                                        <span className="font-mono text-ink-dim">× {it.quantity}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 flex justify-end">
                                <Link to="/shop" className="overline text-neon link-reveal inline-flex items-center gap-2">
                                    REORDER FROM SHOP →
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                <p className="mt-12 text-xs text-ink-mute leading-relaxed">
                    Lost your order #? It's in the subject line of your "Order confirmed" email from <span className="text-ink">orders@helixorapeptides.com</span>.
                    Still stuck? Reach us via Telegram or X (links in the footer) and we'll dig it up.
                </p>
            </div>
        </div>
    );
}
